import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';

const DEFAULT_TITLE = 'Unsaved Changes';
const DEFAULT_MESSAGE = 'You have unsaved changes. Leave without saving?';

export function useUnsavedChangesPrompt({
  when,
  title = DEFAULT_TITLE,
  message = DEFAULT_MESSAGE,
}: {
  when: boolean;
  title?: string;
  message?: string;
}) {
  const [open, setOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const blocker = useBlocker(when);

  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    pendingActionRef.current = null;
    setOpen(true);
  }, [blocker.state]);

  const proceed = useCallback(() => {
    setOpen(false);
    if (blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, [blocker]);

  const cancel = useCallback(() => {
    setOpen(false);
    pendingActionRef.current = null;
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);

  const requestLeave = useCallback(
    (action: () => void) => {
      if (!when) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setOpen(true);
    },
    [when]
  );

  return {
    open,
    title,
    message,
    requestLeave,
    cancel,
    confirm: proceed,
    proceedAfterSave: proceed,
  };
}
